import type { SlideDeck } from './slideData';
import { BAND1_MONTHLY, BAND1_ANNUAL, ONBOARDING_BASE, MIN_USERS } from '../lib/pricingConstants';

const customCSS = `
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');

.dark-theme .reveal { font-family: 'DM Sans', sans-serif; }
.dark-theme .reveal h1, .dark-theme .reveal h2 {
	font-family: 'DM Sans', sans-serif;
	color: #e8e8f0;
	text-transform: none;
	letter-spacing: -0.02em;
	font-weight: 700;
}
.dark-theme .reveal h1 { font-size: 2.6em; line-height: 1.1; }
.dark-theme .reveal h2 { font-size: 1.8em; line-height: 1.2; margin-bottom: 0.6em; }
.dark-theme .reveal h3 { font-size: 1.2em; line-height: 1.3; color: #E8491C; font-weight: 600; font-family: 'DM Sans', sans-serif; }
.dark-theme .reveal p, .dark-theme .reveal li { font-size: 0.72em; line-height: 1.6; color: #f0f0f5; }
.dark-theme .reveal ul { list-style: none; padding-left: 0; }
.dark-theme .reveal ul li { padding: 0.3em 0 0.3em 1.4em; position: relative; }
.dark-theme .reveal ul li::before { content: ''; position: absolute; left: 0; top: 0.75em; width: 6px; height: 6px; border-radius: 50%; background: #E8491C; }
.dark-theme .reveal .slides section { padding: 40px 60px; text-align: left; }

.regulatory-accent { width: 60px; height: 4px; background: #E8491C; border-radius: 2px; margin-bottom: 1.2em; }

.regulatory-bq { background: rgba(255,255,255,0.04); border-left: 4px solid #E8491C; padding: 0.8em 1.2em; margin: 0.8em 0; border-radius: 0 8px 8px 0; width: 90%; box-shadow: none; }
.regulatory-bq p { color: #f0f0f5 !important; font-size: 0.78em !important; font-weight: 400; }

.regulatory-section { text-align: center !important; }
.regulatory-section-num { font-family: 'JetBrains Mono', monospace; font-size: 0.6em; color: #E8491C; font-weight: 500; margin-bottom: 0.4em; display: block; }

.regulatory-icon { display: inline-flex; align-items: center; justify-content: center; width: 44px; height: 44px; border-radius: 10px; font-size: 1.2em; margin-bottom: 12px; }
.regulatory-icon-orange { background: rgba(232,73,28,0.15); }
.regulatory-icon-blue { background: rgba(59,130,246,0.15); }
.regulatory-icon-green { background: rgba(34,197,94,0.15); }
.regulatory-icon-amber { background: rgba(245,158,11,0.15); }

.regulatory-card-row { display: flex; gap: 20px; margin-top: 0.8em; }
.regulatory-card { flex: 1; background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.06); border-radius: 12px; padding: 24px; }
.regulatory-card h3 { margin-top: 0; margin-bottom: 0.3em; font-size: 1em; }
.regulatory-card .card-q { font-size: 0.65em; color: #9898b0; font-style: italic; margin-bottom: 0.6em; }
.regulatory-card p, .regulatory-card li { font-size: 0.62em; }

.regulatory-pricing-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; margin-top: 0.8em; }
.regulatory-price-card { background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.06); border-radius: 12px; padding: 24px; text-align: center; }
.regulatory-price-card.highlight { border-color: #E8491C; background: rgba(232,73,28,0.06); }
.regulatory-price-card .label { font-size: 0.6em; color: #9898b0; text-transform: uppercase; letter-spacing: 0.08em; font-weight: 600; }
.regulatory-price-card .amount { font-size: 1.8em; font-weight: 700; color: #e8e8f0; margin: 0.2em 0; }
.regulatory-price-card .amount .currency { font-size: 0.6em; vertical-align: top; }
.regulatory-price-card .detail { font-size: 0.55em; color: #9898b0; }

.regulatory-big-stat { font-size: 1.95em; font-weight: 700; color: #E8491C; line-height: 1; margin: 0.2em 0; }

.regulatory-flow { display: flex; align-items: center; gap: 0; margin-top: 1em; justify-content: center; }
.regulatory-flow-step { background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.08); border-radius: 10px; padding: 16px 20px; text-align: center; min-width: 140px; }
.regulatory-flow-step .name { font-size: 0.7em; font-weight: 600; color: #e8e8f0; }
.regulatory-flow-step .desc { font-size: 0.52em; color: #9898b0; margin-top: 4px; }
.regulatory-flow-arrow { font-size: 1.2em; color: #E8491C; padding: 0 8px; }

.regulatory-demo-badge { display: inline-block; background: #E8491C; color: white; font-size: 0.5em; font-weight: 600; padding: 4px 14px; border-radius: 20px; text-transform: uppercase; letter-spacing: 0.1em; margin-bottom: 0.6em; }

.regulatory-cta { background: linear-gradient(135deg, rgba(232,73,28,0.1), rgba(232,73,28,0.03)); border: 1px solid rgba(232,73,28,0.2); border-radius: 16px; padding: 32px 40px; margin-top: 1em; }
.regulatory-cta p { color: #f0f0f5 !important; font-size: 0.78em !important; }

.regulatory-compact li { padding: 0.15em 0 0.15em 1.4em !important; }
.regulatory-compact li::before { top: 0.55em !important; }
`;

// Derived pricing for slides (5-8 analysts, monthly, no annual discount)
const saasLow = (5 * BAND1_MONTHLY * 12).toLocaleString();
const saasHigh = (8 * BAND1_MONTHLY * 12).toLocaleString();
const yearOneLow = (5 * BAND1_MONTHLY * 12 + ONBOARDING_BASE).toLocaleString();
const yearOneHigh = (8 * BAND1_MONTHLY * 12 + ONBOARDING_BASE).toLocaleString();
const onboardingDisplay = ONBOARDING_BASE.toLocaleString();

const minMonthly = (MIN_USERS * BAND1_MONTHLY).toLocaleString();

export const regulatoryPresentation: SlideDeck = {
	slug: 'livewire-regulatory-oversight',
	name: 'Regulatory Presentation',
	subtitle: 'Enabling independent regulatory oversight',
	color: '#1a1a2e',
	darkTheme: true,
	customCSS,
	slides: [
		// 1 — Title
		{
			title: '',
			background: '#1a1a2e',
			content: `
				<h1 style="font-size:3em;margin-bottom:0.1em;font-family:lvwr-ubuntu-thin,sans-serif;">
					<span style="color:#E8491C;font-weight:700;">Livewire</span>
				</h1>
				<p style="font-size:1.1em;color:#f0f0f5;font-weight:400;margin-top:0.3em;margin-bottom:1.5em;">
					Enabling Independent Regulatory Oversight
				</p>
				<div  class="fragment" style="font-size:0.6em;color:#f0f0f5;border-top:1px solid rgba(255,255,255,0.08);padding-top:1em;margin-top:1em;">
					<br>Rhys Stewart <br> Auchindown<br>
				</div>`,
		},
		// 2 — Section 01
		{
			title: '',
			background: '#1a1a2e',
			content: `
				<div class="regulatory-section">
					<span class="regulatory-section-num">01</span>
					<h2 style="font-size:2.2em;">The Challenge</h2>
					<p style="max-width:600px;margin:0 auto;">Information asymmetry in regulatory oversight</p>
				</div>`,
		},
		// 3 — The problem
		{
			title: '',
			background: '#1a1a2e',
			content: `
				<div class="regulatory-accent"></div>
				<h2>Utility regulators make crucial decisions, often with limited independent visibility</h2>
				<ul>
					<li class="fragment">Rate cases, performance evaluations, disaster preparedness, infrastructure investment oversight</li>
					<li class="fragment">These decisions are informed almost entirely by data that flows through the entity being regulated</li>
					<li class="fragment">Not a criticism — a structural reality. The regulator is dependent on the regulated to share not only grid information, but also analysis</li>
					<li class="fragment">What's provided are summaries and reports — the underlying methods and calculations are usually a black box</li>
				</ul>`,
		},
		// 4 — Hurricane Melissa
		{
			title: '',
			background: '#1a1a2e',
			content: `
				<div class="regulatory-accent"></div>
				<h2>Consider the next major weather event...</h2>
				<p style="font-size:0.85em;max-width:700px;" fragment >
					After hurricanes Regulators need to understand:
				</p>
				<ul>
					<li class="fragment">Scope and extent of damage</li>
					<li class="fragment">Progress and speed of restoration</li>
					<li class="fragment">Ongoing network condition</li>
				</ul>

				<div class="regulatory-bq fragment">
					<p>How much of that picture depends on the utility telling you what was happening?</p>
				</div>
				<p class="fragment" style="margin-top:1.2em;font-size:0.85em;color:#E8491C;font-weight:600;">
					What if the Regulator has its own independent window into the network?
				</p>`,
		},
		// 5 — Next major weather event
		// 6 — Section 02
		{
			title: '',
			background: '#1a1a2e',
			content: `
				<div class="regulatory-section">
					<span class="regulatory-section-num">02</span>
					<h2 style="font-size:2.2em;">What is <span style="font-family:lvwr-ubuntu-thin,sans-serif;color:#E8491C;">Livewire</span>?</h2>
					<p style="max-width:600px;margin:0 auto;">A spatial digital twin for regulatory intelligence</p>
				</div>`,
		},
		// 6 — Digital twin
		{
			title: '',
			background: '#1a1a2e',
			content: `
				<div class="regulatory-accent"></div>
				<h2>A cloud-based spatial digital twin</h2>
				<ul>
					<li class="fragment">A platform for <strong style="color:#e8e8f0;">seeing, tracking, and analyzing</strong> network state <u>independently</u></li>
					<li class="fragment">Not an OMS or am ADMS — it does not replace or compete with the utility's operational tools</li>
					<li class="fragment">Browser-based — no GIS software, no desktop installation</li>
					<li class="fragment">Mobile ready - inspect assets and annotate findings directly from a phone or tablet in the field</li>
				</ul>
				<div class="regulatory-bq fragment" style="margin-top:1em;">
					<p>The utility has tools for <em>operating</em> the network.<br>
					Livewire gives regulators a tool for <em>overseeing</em> it.</p>
				</div>`,
		},
		// 7 — Section 03
		{
			title: '',
			background: '#1a1a2e',
			content: `
				<div class="regulatory-section">
					<span class="regulatory-section-num">03</span>
					<h2 style="font-size:2.2em;">The Regulator's Toolkit</h2>
					<p style="max-width:600px;margin:0 auto;">Six capabilities. One oversight loop.</p>
				</div>`,
		},
		// 8 — Spotlight
		{
			title: '',
			background: '#1a1a2e',
			content: `
				<div class="regulatory-icon regulatory-icon-orange">🔍</div>
				<h3>Spotlight — "What's there?"</h3>
				<p style="font-size:0.78em;margin-bottom:0.6em;">
					Draw a boundary around any area — a parish, a feeder zone, a community — and instantly see what's inside.
				</p>
				<ul>
					<li class="fragment">Asset counts, types, conditions, connectivity — in seconds</li>
					<li class="fragment">No request to the utility. No waiting for a report.</li>
				</ul>
				<p class="fragment" style="margin-top:0.8em;font-size:0.65em;">
					<strong style="color:#E8491C;">Regulatory use:</strong> Consumer complaints, service equity across parishes, ad-hoc investigations
				</p>`,
		},
		// 9 — Tracing
		{
			title: '',
			background: '#1a1a2e',
			content: `
				<div class="regulatory-icon" style="background:rgba(139,92,246,0.15);">⚡</div>
				<h3>Tracing — "What's connected?"</h3>
				<p style="font-size:0.78em;margin-bottom:0.6em;">
					Select any point on the network and trace upstream to the source or downstream to every connected customer — by phase.
				</p>
				<ul>
					<li class="fragment">How many customers are downstream of an aging switch or conductor?</li>
					<li class="fragment">Is this feeder balanced across phases — or is load concentrated on one?</li>
					<li class="fragment">What's the full path from substation to a specific transformer?</li>
				</ul>
				<p class="fragment" style="margin-top:0.8em;font-size:0.65em;">
					<strong style="color:#E8491C;">Regulatory use:</strong> Assessing customer impact of infrastructure failures, verifying phase balance claims, understanding network exposure
				</p>`,
		},
		// 10 — Chronicle
		{
			title: '',
			background: '#1a1a2e',
			content: `
				<div class="regulatory-icon regulatory-icon-blue">📋</div>
				<h3>Chronicle — "What changed?"</h3>
				<p style="font-size:0.78em;margin-bottom:0.6em;">
					Track every change to the network over time. Compare any two points in history.
				</p>
				<ul>
					<li class="fragment">Verify infrastructure upgrades independently</li>
					<li class="fragment">Pre-storm baseline vs. post-storm reality — spatial evidence, not damage reports</li>
				</ul>
				<p class="fragment" style="margin-top:0.8em;font-size:0.65em;">
					<strong style="color:#E8491C;">Regulatory use:</strong> Rate case verification, post-disaster assessment, infrastructure investment tracking
				</p>`,
		},
		// 10 — Annotations
		{
			title: '',
			background: '#1a1a2e',
			content: `
				<div class="regulatory-icon regulatory-icon-green">📌</div>
				<h3>Annotations — "What did we find?"</h3>
				<p style="font-size:0.78em;margin-bottom:0.6em;">
					Send staff to the field. Inspect assets. Record findings directly in Livewire.
				</p>
				<ul>
					<li class="fragment">Flag discrepancies — missing equipment, mismatched records, unreported conditions</li>
					<li class="fragment">Photos, comments, geolocated and timestamped, tied to the specific asset</li>
				</ul>
				<p class="fragment" style="margin-top:0.8em;font-size:0.65em;">
					<strong style="color:#E8491C;">Regulatory use:</strong> Field verification, compliance audits, independent evidence for rate case reviews
				</p>`,
		},
		// 11 — Reliability Metrics
		{
			title: '',
			background: '#1a1a2e',
			content: `
				<div class="regulatory-icon" style="background:rgba(168,85,247,0.15);">📊</div>
				<h3>Reliability Metrics — "How is the utility performing?"</h3>
				<p style="font-size:0.78em;margin-bottom:0.6em;">
					Calculate SAIDI, SAIFI, and CAIDI independently — from the network model, not from utility-submitted reports.
				</p>
				<ul>
					<li class="fragment">Derive reliability indices directly from network topology and outage data</li>
					<li class="fragment">Break down performance by feeder, parish, or any spatial boundary</li>
					<li class="fragment">Compare the utility's reported metrics against independently calculated values</li>
				</ul>
				<p class="fragment" style="margin-top:0.8em;font-size:0.65em;">
					<strong style="color:#E8491C;">Regulatory use:</strong> Performance benchmarking, rate case evidence, identifying underserved areas, holding licensees accountable to service standards
				</p>`,
		},
		// 12 — Network Baseline
		{
			title: '',
			background: '#1a1a2e',
			content: `
				<div class="regulatory-icon regulatory-icon-amber">🏗️</div>
				<h3>Network Baseline — "What's the truth?"</h3>
				<p style="font-size:0.78em;margin-bottom:0.6em;">
					The Regulator's own record of network state — independent, timestamped, versioned, and queryable.
				</p>
				<div class="regulatory-bq fragment">
					<p>When there's a dispute about network condition or investment, you're not comparing claims. You're comparing data.</p>
				</div>`,
		},
		// 12 — Oversight loop
		{
			title: '',
			background: '#1a1a2e',
			content: `
				<div class="regulatory-accent"></div>
				<h2>The oversight loop</h2>
				<div class="regulatory-flow" style="justify-content:center;gap:4px;">
					<div class="regulatory-flow-step" style="min-width:0;padding:12px 10px;">
						<div class="name">Spotlight</div>
						<div class="desc">What should<br>be there?</div>
					</div>
					<div class="regulatory-flow-arrow">→</div>
					<div class="regulatory-flow-step" style="min-width:0;padding:12px 10px;">
						<div class="name">Tracing</div>
						<div class="desc">What's<br>connected?</div>
					</div>
					<div class="regulatory-flow-arrow">→</div>
					<div class="regulatory-flow-step" style="min-width:0;padding:12px 10px;">
						<div class="name">Chronicle</div>
						<div class="desc">What<br>changed?</div>
					</div>
					<div class="regulatory-flow-arrow">→</div>
					<div class="regulatory-flow-step" style="min-width:0;padding:12px 10px;">
						<div class="name">Annotations</div>
						<div class="desc">What's on<br>the ground?</div>
					</div>
					<div class="regulatory-flow-arrow">→</div>
					<div class="regulatory-flow-step" style="min-width:0;padding:12px 10px;">
						<div class="name">Reliability</div>
						<div class="desc">How are they<br>performing?</div>
					</div>
					<div class="regulatory-flow-arrow">→</div>
					<div class="regulatory-flow-step" style="min-width:0;padding:12px 10px;border-color:#E8491C;">
						<div class="name" style="color:#E8491C;">Baseline</div>
						<div class="desc">The verified<br>truth</div>
					</div>
				</div>
				<p style="text-align:center;margin-top:1.2em;font-size:0.72em;">
					Independent. Continuous. Yours.
				</p>`,
		},
		// 13 — Section 04 Demo
		{
			title: '',
			background: '#0f0f1e',
			content: `
				<div class="regulatory-section">
					<span class="regulatory-section-num">04</span>
					<div class="regulatory-demo-badge">Live Demo</div>
					<h2 style="font-size:2.2em;">Everglades Electric</h2>
					<p style="max-width:600px;margin:0 auto;">A demonstration utility network — see what regulatory oversight looks like in Livewire</p>
				</div>`,
		},
		// Closing
		{
			title: '',
			background: '#1a1a2e',
			content: `
				<div class="regulatory-section">
					<h1 style="margin-bottom:0.2em;font-size:3em;font-family:lvwr-ubuntu-thin,sans-serif;">
						<span style="color:#E8491C;">Livewire</span>
					</h1>
					<p style="font-size:0.85em;color:#9898b0;margin-bottom:1.5em;">Enabling independent regulatory oversight</p>
					<p style="font-size:0.7em;color:#e8e8f0;">
						Rhys Stewart<br>
						<span style="color:#9898b0;">rhys@auchindown.com · lvwr.io</span>
					</p>
				</div>`,
			verticalSlides: [
				// {
				// 	title: '',
				// 	background: '#1a1a2e',
				// 	content: `
				// 		<div class="regulatory-section">
				// 			<span class="regulatory-section-num">05</span>
				// 			<h2 style="font-size:2.2em;">Investment</h2>
				// 		</div>`,
				// },
				{
					title: '',
					background: '#1a1a2e',
					content: `
						<div class="regulatory-accent"></div>
						<h2>Simple, accessible pricing</h2>
						<div class="regulatory-card" style="text-align:center;border-color:rgba(232,73,28,0.3);background:rgba(232,73,28,0.06);">
							<p class="card-q">Starting price</p>
							<div class="regulatory-big-stat" style="font-size:3.2em;">$${minMonthly}<span style="font-size:0.28em;color:#9898b0;font-weight:600;"> / month</span></div>
							<p>Minimum ${MIN_USERS} users at $${BAND1_MONTHLY}/user, then scale up</p>
						</div>
						<div class="regulatory-card-row" style="margin-top:16px;">
							<div class="regulatory-card" style="text-align:center;">
								<p class="card-q">Monthly</p>
								<div class="regulatory-big-stat">$${BAND1_MONTHLY}</div>
								<p>per user / month</p>
							</div>
							<div class="regulatory-card" style="text-align:center;">
								<p class="card-q">Annual commitment</p>
								<div class="regulatory-big-stat">$${BAND1_ANNUAL}</div>
								<p>per user / month · 10+ users</p>
							</div>
						</div>
						<p style="margin-top:0.9em;font-size:0.62em;text-align:center;">
							100 GB storage included · No GIS licensing required · Browser-based
						</p>`,
				},
				{
					title: '',
					background: '#1a1a2e',
					content: `
						<div class="regulatory-accent"></div>
						<h2>For 5 to 8 analysts</h2>
						<div class="regulatory-card-row">
							<div class="regulatory-card" style="text-align:center;">
								<p class="card-q">SaaS</p>
								<div class="regulatory-big-stat">$${saasLow} – $${saasHigh}</div>
								<p>per year</p>
							</div>
							<div class="regulatory-card" style="text-align:center;">
								<p class="card-q">Onboarding (one-time)</p>
								<div class="regulatory-big-stat">$${onboardingDisplay}+</div>
								<p>Jumpstart — 2 weeks to go-live</p>
							</div>
						</div>
						<div class="regulatory-card" style="margin-top:16px;text-align:center;border-color:rgba(232,73,28,0.2);">
							<p class="card-q">Year 1 all-in</p>
							<div class="regulatory-big-stat">$${yearOneLow} – $${yearOneHigh}</div>
							<p>Less than a single consultant engagement — permanent, continuous oversight capability</p>
						</div>`,
				},
				// {
				// 	title: '',
				// 	background: '#1a1a2e',
				// 	content: `
				// 		<div class="regulatory-accent"></div>
				// 		<h2>Proposed next step</h2>
				// 		<div class="regulatory-cta">
				// 			<p><strong style="color:#E8491C;">A pilot.</strong></p>
				// 			<p>Load a subset of the JPS network into a dedicated OUR instance of Livewire. Your team gets hands-on access. You evaluate whether this changes how you see the network.</p>
				// 			<p style="margin-top:0.6em;">If it does, we formalize the engagement.</p>
				// 		</div>`,
				// },
			],
		},
	],
};
